import { useState, useEffect } from "react";
import {
  X,
  Download,
  FileText,
  User,
  Euro,
  Receipt,
  Users,
  Calculator,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";

import { exportPayslip } from "@/services/pdfExport.service";

export default function PayslipViewer({ payslip, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const { success: showSuccess, error: showError } = useToast();

  // Add body class when modal opens
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await exportPayslip(payslip);
      showSuccess("Bulletin de paie téléchargé avec succès !");
    } catch (error) {
      console.error("Error downloading payslip:", error);
      showError("Erreur lors du téléchargement du bulletin de paie.");
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("fr-MA", {
      style: "currency",
      currency: "MAD",
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-MA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatutColor = (statut) => {
    const colors = {
      brouillon: "bg-gray-100 text-gray-800",
      valide: "bg-green-100 text-green-800",
      paye: "bg-blue-100 text-blue-800",
    };
    return colors[statut] || colors.brouillon;
  };

  if (!payslip) return null;

  return (
    <div
      className="modal-backdrop-full flex items-center justify-center p-4"
      onClick={(e) => {
        // Fermer le modal si on clique sur l'overlay (pas sur le contenu)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full h-[85vh] flex flex-col relative z-[10000]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center space-x-3">
            <FileText className="h-8 w-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Bulletin de Paie Marocain
              </h2>
              <p className="text-gray-600">
                {payslip.employe?.prenom} {payslip.employe?.nom} -{" "}
                {formatDate(payslip.periode_debut)} au{" "}
                {formatDate(payslip.periode_fin)}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className={getStatutColor(payslip.statut)}>
              {payslip.statut === "brouillon" && "Brouillon"}
              {payslip.statut === "valide" && "Validé"}
              {payslip.statut === "paye" && "Payé"}
            </Badge>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>
                {downloading ? "Téléchargement..." : "Télécharger PDF"}
              </span>
            </Button>
            <Button variant="outline" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Employee Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Informations Employé</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nom complet</p>
                  <p className="font-medium">
                    {payslip.employe?.prenom} {payslip.employe?.nom}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Département</p>
                  <p className="font-medium">
                    {payslip.employe?.departement?.nom}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Numéro employé</p>
                  <p className="font-medium">
                    {payslip.employe?.numero_employe}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Période</p>
                  <p className="font-medium">
                    {formatDate(payslip.periode_debut)} -{" "}
                    {formatDate(payslip.periode_fin)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Euro className="h-5 w-5" />
                <span>Détail du Salaire</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Base Salary */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Salaire de base</p>
                    <p className="font-medium text-lg">
                      {formatCurrency(payslip.salaire_base)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Heures travaillées</p>
                    <p className="font-medium">
                      {payslip.heures_travaillees || 151.67} h
                    </p>
                  </div>
                </div>

                {/* Overtime */}
                {payslip.heures_supplementaires > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        Heures supplémentaires
                      </p>
                      <p className="font-medium">
                        {payslip.heures_supplementaires} h
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Taux heures sup.</p>
                      <p className="font-medium">{payslip.taux_heures_sup}x</p>
                    </div>
                  </div>
                )}

                {/* Primes */}
                {payslip.primes > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Primes</p>
                    <p className="font-medium text-lg text-green-600">
                      +{formatCurrency(payslip.primes)}
                    </p>
                  </div>
                )}

                <Separator />

                {/* Gross Salary */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Salaire Brut</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(payslip.salaire_brut)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Moroccan Social Contributions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Receipt className="h-5 w-5" />
                <span>Cotisations Sociales Marocaines</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>CNSS (5.5%)</span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(payslip.cotisations_cnss)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>AMO (2%)</span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(payslip.cotisations_amo)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Retraite (1%)</span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(payslip.cotisations_retraite)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-red-600">
                  <span>Total Cotisations</span>
                  <span>-{formatCurrency(payslip.cotisations_sociales)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Expenses and Deductions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calculator className="h-5 w-5" />
                <span>Déductions et Frais</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Frais professionnels (20%)</span>
                  <span className="text-orange-600 font-medium">
                    -{formatCurrency(payslip.frais_professionnels)}
                  </span>
                </div>
                {payslip.autres_deductions > 0 && (
                  <div className="flex justify-between">
                    <span>Autres déductions</span>
                    <span className="text-red-600 font-medium">
                      -{formatCurrency(payslip.autres_deductions)}
                    </span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Salaire Net Imposable</span>
                  <span className="text-lg">
                    {formatCurrency(payslip.salaire_net_imposable)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Moroccan Income Tax */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Impôt sur le Revenu (IR)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>IR Brut</span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(payslip.ir_brut)}
                  </span>
                </div>
                {payslip.nombre_personnes_charge > 0 && (
                  <div className="flex justify-between">
                    <span>
                      Déduction personnes à charge (
                      {payslip.nombre_personnes_charge} pers.)
                    </span>
                    <span className="text-green-600 font-medium">
                      +{formatCurrency(payslip.deduction_personnes)}
                    </span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold text-red-600">
                  <span>IR Net</span>
                  <span>-{formatCurrency(payslip.ir_net)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Final Net Salary */}
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-green-800">
                <Euro className="h-5 w-5" />
                <span>Salaire Net Final</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-700">
                  {formatCurrency(payslip.salaire_net)}
                </p>
                <p className="text-sm text-green-600 mt-2">
                  Montant à verser à l'employé
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">
                Informations Complémentaires
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Date de génération</p>
                  <p>{formatDate(payslip.date_generation)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Statut</p>
                  <Badge className={getStatutColor(payslip.statut)}>
                    {payslip.statut === "brouillon" && "Brouillon"}
                    {payslip.statut === "valide" && "Validé"}
                    {payslip.statut === "paye" && "Payé"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer - Fixed */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={onClose}>OK</Button>
        </div>
      </div>
    </div>
  );
}
